import axios, { AxiosInstance } from "axios";
import { getCrmToken } from "../auth/tokenHelper";

const CRM_BASE_URL = `https://${process.env.DYNAMICS_ORG_URL}/api/data/v9.2`;

function sanitizeODataString(value: string): string {
  return value.replace(/'/g, "''");
}

class DataverseClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: CRM_BASE_URL,
      headers: {
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
        Prefer: "odata.include-annotations=*",
      },
    });

    this.client.interceptors.request.use(async (config) => {
      const token = await getCrmToken();
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.data?.error?.message) {
          const msg = error.response.data.error.message;
          console.error("Dataverse error:", msg);
          return Promise.reject(new Error(msg));
        }
        return Promise.reject(error);
      }
    );
  }

  async findContactByEmail(email: string) {
    const safe = sanitizeODataString(email);
    const response = await this.client.get("/contacts", {
      params: {
        $filter: `emailaddress1 eq '${safe}' or emailaddress2 eq '${safe}'`,
        // _parentcustomerid_value annotation is returned automatically via
        // Prefer: odata.include-annotations=* — no $expand needed (and
        // expanding a polymorphic lookup causes a 400 in Dynamics 365)
        $select: "contactid,fullname,emailaddress1,telephone1,jobtitle,_parentcustomerid_value",
      },
    });
    return response.data.value;
  }

  async findLeadByEmail(email: string) {
    const safe = sanitizeODataString(email);
    const response = await this.client.get("/leads", {
      params: {
        $filter: `emailaddress1 eq '${safe}'`,
        $select: "leadid,fullname,emailaddress1,telephone1,subject,companyname,statuscode",
      },
    });
    return response.data.value;
  }

  async getOpportunitiesForContact(contactId: string, accountId?: string) {
    const filters = [`_parentcontactid_value eq '${contactId}'`];
    if (accountId) {
      filters.push(`_parentaccountid_value eq '${accountId}'`);
      filters.push(`_customerid_value eq '${accountId}'`);
      filters.push(`_westcom_distributor_value eq '${accountId}'`);
    }
    const response = await this.client.get("/opportunities", {
      params: {
        $filter: filters.join(" or "),
        $select: "opportunityid,name,estimatedvalue,closeprobability,statuscode,estimatedclosedate",
        $orderby: "estimatedclosedate desc",
        $top: 10,
      },
    });
    return response.data.value;
  }

  async createEmailActivity(emailData: {
    subject: string;
    description: string;
    senderEmail: string;
    recipientEmails: string[];
    regardingObjectId?: string;
    regardingObjectType?: string;
  }) {
    const payload: Record<string, unknown> = {
      subject: emailData.subject,
      description: emailData.description,
      directioncode: false,
      email_activity_parties: [
        {
          participationtypemask: 1,
          addressused: emailData.senderEmail,
        },
        ...emailData.recipientEmails.map((email) => ({
          participationtypemask: 2,
          addressused: email,
        })),
      ],
    };

    if (emailData.regardingObjectId && emailData.regardingObjectType) {
      const entitySetName = emailData.regardingObjectType === "opportunity"
        ? "opportunities"
        : `${emailData.regardingObjectType}s`;
      payload[`regardingobjectid_${emailData.regardingObjectType}@odata.bind`] =
        `/${entitySetName}(${emailData.regardingObjectId})`;
    }

    const response = await this.client.post("/emails", payload);
    return response.headers["odata-entityid"];
  }

  async createContact(data: {
    firstname: string;
    lastname: string;
    emailaddress1: string;
    telephone1?: string;
    jobtitle?: string;
  }) {
    const response = await this.client.post("/contacts", data);
    return response.headers["odata-entityid"];
  }

  async createLead(data: Record<string, any>) {
    const response = await this.client.post("/leads", data);
    return response.headers["odata-entityid"];
  }

  async createAccount(data: {
    name: string;
    emailaddress1?: string;
    telephone1?: string;
    websiteurl?: string;
  }) {
    const response = await this.client.post("/accounts", data);
    return response.headers["odata-entityid"];
  }

  async createAppointment(data: {
    subject: string;
    scheduledstart: string;
    scheduledend: string;
    description?: string;
    regardingObjectId?: string;
    regardingObjectType?: string;
  }) {
    const payload: Record<string, unknown> = {
      subject: data.subject,
      scheduledstart: data.scheduledstart,
      scheduledend: data.scheduledend,
      description: data.description,
    };

    if (data.regardingObjectId && data.regardingObjectType) {
      payload[`regardingobjectid_${data.regardingObjectType}@odata.bind`] =
        `/${data.regardingObjectType}s(${data.regardingObjectId})`;
    }

    const response = await this.client.post("/appointments", payload);
    return response.headers["odata-entityid"];
  }

  async searchAccounts(query: string) {
    const safe = sanitizeODataString(query);
    const response = await this.client.get("/accounts", {
      params: {
        $filter: `contains(name,'${safe}') or contains(emailaddress1,'${safe}') or contains(accountnumber,'${safe}')`,
        $select: "accountid,name,emailaddress1,telephone1,accountnumber",
        $top: 10,
        $orderby: "name asc",
      },
    });
    return response.data.value;
  }

  async searchDistributors(query: string) {
    const safe = sanitizeODataString(query);
    const response = await this.client.get("/accounts", {
      params: {
        $filter: `westcom_accounttype eq 119380000 and (contains(name,'${safe}') or contains(emailaddress1,'${safe}') or contains(accountnumber,'${safe}'))`,
        $select: "accountid,name,emailaddress1,telephone1,accountnumber",
        $top: 10,
        $orderby: "name asc",
      },
    });
    return response.data.value;
  }

  async searchOpportunities(query: string) {
    const safe = sanitizeODataString(query);
    const response = await this.client.get("/opportunities", {
      params: {
        $filter: `contains(name,'${safe}')`,
        $select: "opportunityid,name,estimatedvalue,statuscode,_parentaccountid_value",
        $top: 10,
        $orderby: "name asc",
      },
    });
    return response.data.value;
  }

  async globalSearch(query: string) {
    const response = await this.client.get("/search", {
      params: {
        search: query,
        entities: "contact,lead,account,opportunity",
        top: 20,
      },
    });
    return response.data.value;
  }
  async getPicklistOptions(entityName: string, attributeName: string, isMultiSelect = false) {
    const type = isMultiSelect
      ? "MultiSelectPicklistAttributeMetadata"
      : "PicklistAttributeMetadata";
    const response = await this.client.get(
      `/EntityDefinitions(LogicalName='${entityName}')/Attributes/Microsoft.Dynamics.CRM.${type}`,
      {
        params: {
          $filter: `LogicalName eq '${attributeName}'`,
          $expand: "OptionSet",
        },
      }
    );
    const attr = response.data.value[0];
    if (!attr?.OptionSet?.Options) return [];
    return attr.OptionSet.Options.map((o: any) => ({
      value: o.Value as number,
      label: (o.Label?.UserLocalizedLabel?.Label ?? `${o.Value}`) as string,
    }));
  }
}

export const dataverseClient = new DataverseClient();
